/*
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

/**
 * Service for managing pods and devices via the Flask API.
 */
angular.module('nsadmin').factory('podService', ['$injector',
    function podService($injector) {

        var flaskApiService = $injector.get('flaskApiService');

        var service = {};

        /**
         * Gets all pods from the Flask API.
         *
         * @returns {Promise}
         *     A promise that resolves with an array of pod objects.
         */
        service.getAllPods = function getAllPods() {
            return flaskApiService.get('/pods');
        };

        /**
         * Creates a new pod.
         *
         * @param {Object} podData
         *     The pod data including pod_id and description.
         *
         * @returns {Promise}
         *     A promise that resolves with the created pod object.
         */
        service.createPod = function createPod(podData) {
            return flaskApiService.post('/pods', podData);
        };

        /**
         * Updates an existing pod.
         *
         * @param {String} podId
         *     The ID of the pod to update.
         *
         * @param {Object} podData
         *     The updated pod data.
         *
         * @returns {Promise}
         *     A promise that resolves with the updated pod object.
         */
        service.updatePod = function updatePod(podId, podData) {
            return flaskApiService.put('/pods/' + podId, podData);
        };

        /**
         * Deletes a pod.
         *
         * @param {String} podId
         *     The ID of the pod to delete.
         *
         * @returns {Promise}
         *     A promise that resolves when the pod is deleted.
         */
        service.deletePod = function deletePod(podId) {
            return flaskApiService.delete('/pods/' + podId);
        };

        /**
         * Gets all devices for a specific pod.
         *
         * @param {String} podId
         *     The ID of the pod.
         *
         * @returns {Promise}
         *     A promise that resolves with an array of pod devices.
         */
        service.getPodDevices = function getPodDevices(podId) {
            return flaskApiService.get('/pods/' + podId + '/devices');
        };

        /**
         * Gets all pod devices across all pods.
         *
         * @returns {Promise}
         *     A promise that resolves with an array of all pod devices.
         */
        service.getAllPodDevices = function getAllPodDevices() {
            return flaskApiService.get('/pod-devices');
        };

        /**
         * Adds a device to a pod.
         *
         * @param {String} podId
         *     The ID of the pod.
         *
         * @param {Object} deviceData
         *     The device data (device_slot, dev_name, ip_address, etc.).
         *
         * @returns {Promise}
         *     A promise that resolves when the device is added.
         */
        service.addPodDevice = function addPodDevice(podId, deviceData) {
            return flaskApiService.post('/pods/' + podId + '/devices', deviceData);
        };

        /**
         * Updates a pod device.
         *
         * @param {String} podId
         *     The ID of the pod.
         *
         * @param {String} deviceSlot
         *     The device slot identifier.
         *
         * @param {Object} deviceData
         *     The updated device data.
         *
         * @returns {Promise}
         *     A promise that resolves when the device is updated.
         */
        service.updatePodDevice = function updatePodDevice(podId, deviceSlot, deviceData) {
            return flaskApiService.put('/pods/' + podId + '/devices/' + deviceSlot, deviceData);
        };

        /**
         * Deletes a pod device.
         *
         * @param {String} podId
         *     The ID of the pod.
         *
         * @param {String} deviceSlot
         *     The device slot identifier.
         *
         * @returns {Promise}
         *     A promise that resolves when the device is deleted.
         */
        service.deletePodDevice = function deletePodDevice(podId, deviceSlot) {
            return flaskApiService.delete('/pods/' + podId + '/devices/' + deviceSlot);
        };

        /**
         * Gets all shared infrastructure devices.
         *
         * @returns {Promise}
         *     A promise that resolves with an array of shared devices.
         */
        service.getSharedDevices = function getSharedDevices() {
            return flaskApiService.get('/shared-devices');
        };

        /**
         * Adds a shared infrastructure device.
         *
         * @param {Object} deviceData
         *     The device data.
         *
         * @returns {Promise}
         *     A promise that resolves when the device is added.
         */
        service.addSharedDevice = function addSharedDevice(deviceData) {
            return flaskApiService.post('/shared-devices', deviceData);
        };

        /**
         * Updates a shared infrastructure device.
         *
         * @param {String} deviceSlot
         *     The device slot identifier.
         *
         * @param {Object} deviceData
         *     The updated device data.
         *
         * @returns {Promise}
         *     A promise that resolves when the device is updated.
         */
        service.updateSharedDevice = function updateSharedDevice(deviceSlot, deviceData) {
            return flaskApiService.put('/shared-devices/' + deviceSlot, deviceData);
        };

        /**
         * Deletes a shared infrastructure device.
         *
         * @param {String} deviceSlot
         *     The device slot identifier.
         *
         * @returns {Promise}
         *     A promise that resolves when the device is deleted.
         */
        service.deleteSharedDevice = function deleteSharedDevice(deviceSlot) {
            return flaskApiService.delete('/shared-devices/' + deviceSlot);
        };

        return service;
    }
]);
